import { Amplify } from "aws-amplify";
import * as AmplifyAuth from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { DEFAULT_USER, USER_ACTIONS_TO_AUTHORIZE_BY_SCOPE, type UserActions } from "hoxton-shared/enums/user";

import { getWppToken } from "@/services/wppAuth";
import store from "@/store";
import { AuthGetters } from "@/store/modules/auth";
import { API_ROUTES } from "@/constants";

export async function logout() {
    // Returns true to confirm the user was logged out
    await AmplifyAuth.signOut();
    return true;
}

export async function login(username: string, password: string) {
    return AmplifyAuth.signIn({ username, password });
}

export async function providerLogin(provider: string) {
    return AmplifyAuth.signInWithRedirect({ provider: { custom: provider } });
}

function getName(
    user: {
        given_name?: string;
        family_name?: string;
        email?: string;
        "cognito:username"?: string;
    },
    firstNameOnly: boolean
) {
    if (firstNameOnly) {
        return user.given_name;
    }

    if (user.given_name && user.family_name) {
        return `${user.given_name} ${user.family_name}`;
    }

    if (user.email) {
        return user.email;
    }

    if (user["cognito:username"]) {
        return user["cognito:username"];
    }

    return null;
}

export async function userDisplayName(firstNameOnly = false) {
    // Get current oauth session and user
    const { tokens } = await AmplifyAuth.fetchAuthSession();

    if (!tokens?.idToken) {
        return DEFAULT_USER;
    }

    // Return default display name if user name not found
    return getName(tokens.idToken.payload as Record<string, string>, firstNameOnly) ?? DEFAULT_USER;
}

export async function userEmail() {
    // Get current oauth session and user
    const { tokens } = await AmplifyAuth.fetchAuthSession();

    if (!tokens?.idToken) {
        return "";
    }

    // Return default display name if user name not found
    return tokens.idToken.payload.email;
}

export async function getPayload() {
    const { tokens } = await AmplifyAuth.fetchAuthSession();

    if (!tokens?.idToken) {
        return {};
    }

    return tokens.idToken.payload;
}

async function getAmplifyToken() {
    try {
        const { tokens } = await AmplifyAuth.fetchAuthSession();

        return tokens?.idToken?.toString() ?? null;
    } catch {
        // No session / refresh failed — return null instead of throwing.
        return null;
    }
}

export async function getIdToken() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (store.getters[AuthGetters.isWppLogin]) {
        return getWppToken();
    }

    return getAmplifyToken();
}

// Fires when Amplify can no longer refresh the session.
export function onSessionLost(handler: () => void) {
    return Hub.listen("auth", ({ payload }) => {
        if (payload.event === "tokenRefresh_failure") {
            handler();
        }
    });
}

export type OauthConfig = {
    userPoolId: string;
    userPoolWebClientId: string;
    ssoURL: string;
    region: string;
    imageURL: string;
    apiURL: string;
};

export async function getOauthConfig() {
    const response = await fetch(API_ROUTES.OAUTH_CONFIG);

    if (response.status !== 200) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const error = await response.json();
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        throw new Error(error.message ?? "Error retrieving Auth Config");
    }

    return response.json() as Promise<OauthConfig>;
}

export function configureAmplify(authConfig: OauthConfig) {
    Amplify.configure({
        Auth: {
            Cognito: {
                userPoolId: authConfig.userPoolId,
                userPoolClientId: authConfig.userPoolWebClientId,
                loginWith: {
                    oauth: {
                        domain: authConfig.ssoURL.replace(/^https?:\/\//, ""),
                        redirectSignIn: [`https://${document.domain}/`],
                        redirectSignOut: [`https://${document.domain}/`],
                        scopes: ["openid"],
                        responseType: "code"
                    }
                }
            }
        }
    });
}

export async function getIsAuthorized(authConfig: OauthConfig) {
    configureAmplify(authConfig);

    const amplifyToken = await getAmplifyToken();

    return !!amplifyToken;
}

export async function forgotPassword(username: string) {
    return AmplifyAuth.resetPassword({ username });
}

export async function forgotPasswordSubmit(username: string, code: string, password: string) {
    return AmplifyAuth.confirmResetPassword({ username, confirmationCode: code, newPassword: password });
}

export async function completeNewPassword(password: string) {
    return AmplifyAuth.confirmSignIn({ challengeResponse: password });
}

export function hasScope(scope: { clientId: string; campaignId?: string }, exact = true) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    return store.getters[AuthGetters.hasScope](scope, exact) as boolean;
}

export function userCan(
    action: UserActions,
    scope: { clientId: string; campaignId?: string } | null = null,
    exact = true
) {
    const isScopeRequired = USER_ACTIONS_TO_AUTHORIZE_BY_SCOPE.includes(action);

    if (isScopeRequired && !scope) {
        throw new Error(`AuthService: Scope required for action: ${action}`);
    }

    return isScopeRequired
        ? // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          ((store.getters[AuthGetters.userCan](action) && scope && hasScope(scope, exact)) as boolean)
        : // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          (store.getters[AuthGetters.userCan](action) as boolean);
}
